import { useMemo, useState, useCallback } from 'react';
import EmptyStateNotice from '@/components/EmptyStateNotice';
import useResponsive from '@/hooks/useResponsive';
import { VscTelescope } from '@/components/react-icons/main';
import TableHeader from './pageTable/TableHeader.jsx';
import TableMobileCard from './pageTable/TableMobileCard.jsx';
import TableRow from './pageTable/TableRow.jsx';
import sortTableData from './pageTable/sortTableData.js';
import Page from './Page.jsx';

const PageTable = ({ columns = [], data = [], sections = [], actions = [], actionButtons, actionMenuFitContent = false, onChange, onActionClick, onRowClick, onSortChange, isLoading, pagination, select = {}, noDataTitle, noDataDescription, mobileCard }) => {
  const [sortConfig, setSortConfig] = useState(null);
  const hasActions = typeof actions === 'function' || actions.length > 0;
  const { mobile } = useResponsive();
  const showCards = !!mobileCard && mobile;

  const handleSort = (key) => {
    let nextSortConfig = null;
    if (!sortConfig) {
      nextSortConfig = { key, direction: 'ascending' };
    } else if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      nextSortConfig = { key, direction: 'descending' };
    }
    setSortConfig(nextSortConfig);
    // Pages that paginate externally (via usePagination) must sort the full dataset
    // themselves: the paginated render path below shows pagination.paginatedData, which
    // this component cannot reorder.
    onSortChange?.(nextSortConfig);
  };

  const sortData = useCallback((dataToSort) => sortTableData(dataToSort, sortConfig), [sortConfig]);

  const sortedData = useMemo(() => {
    return sortData(data);
  }, [data, sortData]);

  const sortedSections = useMemo(() => {
    return sections.map((section) => ({
      ...section,
      data: sortData(section.data || [])
    }));
  }, [sections, sortData]);

  const totalDataLength = sections.length > 0 ? sortedSections.reduce((sum, section) => sum + (section.data?.length || 0), 0) : data.length;

  const renderSectionHeader = (title) => {
    return (
      <div className="table-section-header" style={{ width: '100%' }}>
        <div className="table-section-header-content">{title}</div>
      </div>
    );
  };

  const renderRow = (rowData, index, key) => {
    const rowActions = typeof actions === 'function' ? actions(rowData, index) : actions;
    if (showCards) {
      return <TableMobileCard key={key} data={rowData} card={mobileCard(rowData, index)} actions={rowActions} actionMenuFitContent={actionMenuFitContent} onAction={onActionClick} onRowClick={onRowClick} />;
    }
    return <TableRow key={key} data={rowData} columns={columns} actions={rowActions} actionButtons={actionButtons} actionMenuFitContent={actionMenuFitContent} hasActions={hasActions} onChange={onChange} onAction={onActionClick} onRowClick={onRowClick} select={select} />;
  };

  return (
    <Page.Section header={showCards ? null : <TableHeader columns={columns} onSort={handleSort} sortConfig={sortConfig} hasActions={hasActions} />} className={`comp-page-table ${showCards ? 'mobile-cards' : ''}`} pagination={pagination}>
      {!isLoading && sections.length > 0 && (
        <>
          {sortedSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {section.title && renderSectionHeader(section.title)}
              {section.data?.map((rowData, index) => renderRow(rowData, index, rowData.id || `${sectionIndex}-${index}`))}
            </div>
          ))}
        </>
      )}
      {!isLoading && sections.length === 0 && pagination?.paginatedData?.length > 0 && <>{pagination?.paginatedData?.map((data, index) => renderRow(data, index, data.id || index))}</>}
      {!isLoading && sections.length === 0 && !pagination?.paginatedData?.length && sortedData.map((data, index) => renderRow(data, index, data.id || index))}
      {isLoading && <EmptyStateNotice className="workflow" size={'sm'} loading card={true} title={'Loading data...'} />}
      {!isLoading && totalDataLength === 0 && <EmptyStateNotice className="workflow" size={'sm'} icon={VscTelescope} loading={false} card={true} title={noDataTitle || 'No data yet'} description={noDataDescription || 'Create data to get started'} />}
      <Page.TableKeyHandler row={'.table-row'} />
    </Page.Section>
  );
};

export default PageTable;
